import { useState, useCallback, useMemo } from "react";
import { ToolContainer, FileDropZone, ProcessingState, DownloadButton, downloadBlob, ResultPreview } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { convertImageInBrowser } from "./convertBrowser";
import { parseConvertOptions, type OutFormat } from "./convertCore";
import { DEFAULT_FORM, formToRaw, friendlyOptionError, metaCanBeKept, needsFill, type ConvertForm } from "./convertForm";
import { FillNotice, MoreOptions, QualityControl, optionError as formOptionError, useEncodeSupport } from "./ConvertOptionsPanel";
import { WEBP_UNSUPPORTED_REASON } from "./imageFormats";
import { looksLikeImage, notAnImageMessage, probeImage, unreadableImageMessage, fmtSize, savingPercent } from "./imageCheck";

const TOOL_INFO_CONTENT = {
  what: `Converts an image to or from the WebP format — a modern image format that typically produces smaller file sizes than JPG or PNG at similar visual quality, widely used for faster-loading websites. Drop a JPG or PNG to convert it into WebP with adjustable quality, or drop an existing WebP file to convert it back out to JPG or PNG. Under "More options" you can also resize it, turn or flip it, make it black and white, and keep or remove camera and location details.`,
  how: `Upload your image. If it's a JPG or PNG, adjust the quality and convert to WebP. If it's already a WebP file, pick JPG or PNG as the output format instead. Open "More options" if you also want to change the size or colour. Download the result either way.`,
  tip: `If you're preparing images for a website, WebP is usually the better choice for page-load speed, but keep a JPG or PNG backup if you need broad compatibility with very old software. Converting a WebP with transparency to JPG will lose that transparency, since JPG doesn't support it — PNG keeps it.`,
};

const OUTPUT_FROM_WEBP: readonly { value: OutFormat; label: string; ext: string }[] = [
  { value: "jpeg", label: "JPG", ext: "jpg" },
  { value: "png", label: "PNG", ext: "png" },
];

export default function WebpConverter() {
  const [file, setFile] = useState<File | null>(null);
  const [form, setForm] = useState<ConvertForm>({ ...DEFAULT_FORM, format: "webp", quality: 80 });
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<Blob | null>(null);
  const canEncode = useEncodeSupport();
  const change = useCallback((patch: Partial<ConvertForm>) => { setForm((f) => ({ ...f, ...patch })); setResult(null); }, []);
  const outputFormat = form.format;
  // Real bidirectional support, not just a one-way encoder: convertImage
  // itself already decodes any browser-renderable image format
  // (including WebP) via a plain <img> element under the hood -- the
  // only thing actually missing was UI to route a WebP source to the
  // opposite conversion direction. Detecting the source format here and
  // switching which output options are offered is what makes "to or
  // from WebP" genuinely true, not just claimed.
  const isWebpSource = useMemo(() => file?.type === "image/webp" || /\.webp$/i.test(file?.name ?? ""), [file]);

  const [fileError, setFileError] = useState("");

  const handleFiles = useCallback(async (files: File[]) => {
    const f = files[0];
    if (!f) return;
    setResult(null);
    if (!looksLikeImage(f)) { setFileError(notAnImageMessage(f.name)); return; }
    if (f.size === 0 || !(await probeImage(f))) { setFileError(unreadableImageMessage(f.name)); return; }
    setFileError("");
    // A WebP source goes out as JPG (the usual wish); anything else goes to WebP.
    const fromWebp = f.type === "image/webp" || /\.webp$/i.test(f.name);
    setForm((cur) => ({ ...cur, format: fromWebp ? "jpeg" : "webp", quality: fromWebp ? 92 : 80 }));
    setFile(f);
  }, []);

  const parsed = useMemo(() => parseConvertOptions(formToRaw(form)), [form]);
  const optionError = formOptionError(form, parsed.ok ? null : friendlyOptionError(parsed.error));
  const webpBlocked = outputFormat === "webp" && canEncode["image/webp"] === false;

  const convert = async () => {
    if (!file || !parsed.ok) return;
    setProcessing(true);
    try {
      const blob = await convertImageInBrowser(file, parsed.options);
      if (!blob.size) throw new Error("The conversion produced an empty file. Try another image.");
      setResult(blob);
      const label = outputFormat === "webp" ? "WebP" : OUTPUT_FROM_WEBP.find(f => f.value === outputFormat)?.label ?? outputFormat;
      toast.success(`Converted to ${label}! Size: ${fmtSize(blob.size)}`);
    } catch (e) { toast.error((e as Error)?.message || "Conversion failed"); }
    finally { setProcessing(false); }
  };

  const outputExt = outputFormat === "webp" ? "webp" : (OUTPUT_FROM_WEBP.find(f => f.value === outputFormat)?.ext ?? "jpg");
  const keepable = !!file && metaCanBeKept(outputFormat, file.type);

  return (
    <ToolContainer>
      {!file ? (
        <>
        <FileDropZone gateAtDownload accept="image/*" onFiles={handleFiles} label="Drop an image to convert to or from WebP" description="JPG/PNG converts to WebP — an existing WebP converts to JPG or PNG" />
        {fileError && <p role="alert" className="text-xs text-destructive mt-2" data-testid="webp-file-error">{fileError}</p>}
        </>
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card"><p className="text-sm font-medium">{file.name} ({fmtSize(file.size)})</p></div>

          {isWebpSource ? (
            <div>
              <Label className="mb-1 block text-sm">Convert to</Label>
              <div className="flex gap-2">
                {OUTPUT_FROM_WEBP.map(f => (
                  <Button
                    key={f.value}
                    size="sm"
                    variant={outputFormat === f.value ? "default" : "outline"}
                    onClick={() => change({ format: f.value })}
                  >
                    {f.label}
                  </Button>
                ))}
              </div>
            </div>
          ) : null}
          {webpBlocked && <p role="alert" className="text-xs text-destructive">{WEBP_UNSUPPORTED_REASON}</p>}
          <QualityControl form={form} onChange={change} min={10} />
          <FillNotice form={form} onChange={change} show={needsFill(form) && /\.(png|webp|gif|svg|avif)$/i.test(file.name)} />
          <MoreOptions form={form} onChange={change} metaHelp={keepable ? "Copied into the new JPG." : "Only possible when both the original and the result are JPG. Otherwise this information is removed."} />
          {optionError && <p role="alert" className="text-sm text-destructive">{optionError}</p>}

          {result && (
            <div className="text-sm rounded-md border p-3 space-y-1" data-testid="webp-sizes">
              <p>Before: {fmtSize(file.size)}. After: {fmtSize(result.size)}. {(() => { const p = savingPercent(file.size, result.size); return p >= 0 ? `Saved ${p}%.` : `${-p}% larger than the original.`; })()}</p>
              <p className="text-xs text-muted-foreground">{form.keepMeta && keepable ? "Camera, date and location details were kept in the converted copy." : "Location, camera and other hidden photo data were removed from the converted copy."}</p>
            </div>
          )}
          {result && <ResultPreview blob={result} />}
          <div className="flex gap-3">
            {processing ? <ProcessingState /> : result ? (
              <><DownloadButton blob={result} fileName={file.name.replace(/\.[^.]+$/, `.${outputExt}`)} label={`Download ${outputExt.toUpperCase()}`} /><Button variant="outline" onClick={() => { setFile(null); setResult(null); }}>New</Button></>
            ) : (
              <><Button onClick={convert} disabled={!parsed.ok || !!optionError || webpBlocked}>{isWebpSource ? `Convert to ${OUTPUT_FROM_WEBP.find(f => f.value === outputFormat)?.label}` : "Convert to WebP"}</Button><Button variant="outline" onClick={() => setFile(null)}>Reset</Button></>
            )}
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
